import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Typography,
  Chip,
  LinearProgress,
  Skeleton,
  InputAdornment,
  TextField,
  Tooltip,
  Avatar,
  Stack,
  Divider,
  IconButton,
  Fade,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  WorkOutline as JobIcon,
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  PlayCircleOutline,
  CancelOutlined,
  TrendingUp,
  AccessTime,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import moment from "moment";
import useAPI from "hooks/useAPI";
import useAlert from "hooks/useAlert";
import AlertMessage from "components/UI/AlertMessage";
import PropTypes from "prop-types";

// ─── Status meta ────────────────────────────────────────────────────────────

const STATUS_META = {
  completed: {
    color: "#22c55e",
    bg: "rgba(34,197,94,0.10)",
    icon: <CheckCircleOutline fontSize="small" />,
    label: "Completed",
  },
  failed: {
    color: "#ef4444",
    bg: "rgba(239,68,68,0.10)",
    icon: <ErrorOutline fontSize="small" />,
    label: "Failed",
  },
  running: {
    color: "#3b82f6",
    bg: "rgba(59,130,246,0.10)",
    icon: <PlayCircleOutline fontSize="small" />,
    label: "Running",
  },
  pending: {
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.10)",
    icon: <HourglassEmpty fontSize="small" />,
    label: "Pending",
  },
  cancelled: {
    color: "#6b7280",
    bg: "rgba(107,114,128,0.10)",
    icon: <CancelOutlined fontSize="small" />,
    label: "Cancelled",
  },
  processing: {
    color: "#8b5cf6",
    bg: "rgba(139,92,246,0.10)",
    icon: <PlayCircleOutline fontSize="small" />,
    label: "Processing",
  },
};

const getDominantStatus = (group) => {
  if ((group.running_count || 0) > 0) return "running";
  if ((group.processing_count || 0) > 0) return "processing";

  const counts = {
    completed: group.completed_count || 0,
    failed: group.failed_count || 0,
    cancelled: group.cancelled_count || 0,
  };
  return Object.entries(counts).reduce(
    (best, [status, count]) => (count > counts[best] ? status : best),
    "completed"
  );
};

const getCardAccentGradient = (status) => {
  const gradients = {
    completed: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    failed: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    running: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    pending: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    cancelled: "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)",
    processing: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
  };
  return gradients[status] || gradients.completed;
};

const toTitleCase = (str) => {
  if (!str) return "";
  return str
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// ─── Stat Pill ───────────────────────────────────────────────────────────────

const StatPill = ({ count, label, color, icon }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      px: 1,
      py: 0.4,
      borderRadius: "999px",
      backgroundColor: STATUS_META[color]?.bg || "rgba(0,0,0,0.05)",
      border: `1px solid ${STATUS_META[color]?.color || "#ccc"}22`,
    }}
  >
    <Box sx={{ color: STATUS_META[color]?.color || "#333", display: "flex", fontSize: "0.75rem" }}>
      {icon}
    </Box>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, color: STATUS_META[color]?.color || "#333", lineHeight: 1 }}
    >
      {count}
    </Typography>
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", fontSize: "0.65rem", lineHeight: 1 }}
    >
      {label}
    </Typography>
  </Box>
);

StatPill.propTypes = {
  count: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
};

// ─── Summary Stat Card ────────────────────────────────────────────────────────

const SummaryCard = ({ label, value, icon, gradient, subtitle }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3,
      background: gradient,
      color: "#fff",
      position: "relative",
      overflow: "hidden",
      border: "none",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        right: -20,
        top: -20,
        width: 80,
        height: 80,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.12)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        right: 20,
        bottom: -30,
        width: 100,
        height: 100,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.08)",
      }}
    />
    <CardContent sx={{ position: "relative", zIndex: 1, p: "20px !important" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, lineHeight: 1 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.5, display: "block" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", width: 44, height: 44 }}>
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

SummaryCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  gradient: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

// ─── Job Group Card ───────────────────────────────────────────────────────────

const JobGroupCard = ({ group, onClick }) => {
  const dominantStatus = getDominantStatus(group);
  const meta = STATUS_META[dominantStatus] || STATUS_META.completed;
  const successRate =
    group.total_count > 0
      ? Math.round(((group.completed_count || 0) / group.total_count) * 100)
      : 0;

  return (
    <Fade in timeout={400}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1.5px solid",
          borderColor: "divider",
          overflow: "hidden",
          transition: "all 0.25s ease !important",
          "&:hover": {
            borderColor: meta.color,
            transform: "translateY(-4px)",
            boxShadow: `0 12px 32px ${meta.color}22`,
          },
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardActionArea
          onClick={onClick}
          sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
        >
          {/* Accent stripe */}
          <Box sx={{ height: 4, background: getCardAccentGradient(dominantStatus) }} />

          <CardContent
            sx={{ p: 2.5, flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}
          >
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    background: getCardAccentGradient(dominantStatus),
                    fontSize: "1rem",
                  }}
                >
                  <JobIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ lineHeight: 1.2, color: "text.primary" }}
                  >
                    {toTitleCase(group.name)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {group.total_count || 0} total runs
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={meta.label}
                size="small"
                icon={React.cloneElement(meta.icon, { sx: { fontSize: "0.8rem !important" } })}
                sx={{
                  background: meta.bg,
                  color: meta.color,
                  borderColor: `${meta.color}40`,
                  border: "1px solid",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  height: 24,
                  "& .MuiChip-icon": { color: meta.color },
                }}
              />
            </Stack>

            {/* Progress bar */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <TrendingUp sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Success Rate
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  fontWeight={800}
                  sx={{
                    color:
                      successRate >= 80 ? "#22c55e" : successRate >= 50 ? "#f59e0b" : "#ef4444",
                  }}
                >
                  {successRate}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={successRate}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "rgba(0,0,0,0.06)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    background: getSuccessFailGradient(group),
                    // background:
                    //   successRate >= 80
                    //     ? "linear-gradient(90deg, #22c55e, #16a34a)"
                    //     : successRate >= 50
                    //     ? "linear-gradient(90deg, #f59e0b, #d97706)"
                    //     : "linear-gradient(90deg, #ef4444, #dc2626)",
                  },
                }}
              />
            </Box>

            {/* Stat pills */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {(group.completed_count || 0) > 0 && (
                <StatPill
                  count={group.completed_count}
                  label="Done"
                  color="completed"
                  icon={<CheckCircleOutline sx={{ fontSize: "0.7rem" }} />}
                />
              )}
              {(group.running_count || 0) > 0 && (
                <StatPill
                  count={group.running_count}
                  label="Running"
                  color="running"
                  icon={<PlayCircleOutline sx={{ fontSize: "0.7rem" }} />}
                />
              )}
              {(group.failed_count || 0) > 0 && (
                <StatPill
                  count={group.failed_count}
                  label="Failed"
                  color="failed"
                  icon={<ErrorOutline sx={{ fontSize: "0.7rem" }} />}
                />
              )}
              {(group.pending_count || 0) > 0 && (
                <StatPill
                  count={group.pending_count}
                  label="Pending"
                  color="pending"
                  icon={<HourglassEmpty sx={{ fontSize: "0.7rem" }} />}
                />
              )}
              {(group.cancelled_count || 0) > 0 && (
                <StatPill
                  count={group.cancelled_count}
                  label="Cancelled"
                  color="cancelled"
                  icon={<CancelOutlined sx={{ fontSize: "0.7rem" }} />}
                />
              )}
            </Box>

            <Divider sx={{ borderColor: "divider" }} />

            {/* Footer */}
            <Stack direction="row" alignItems="center" gap={0.5}>
              <AccessTime sx={{ fontSize: 13, color: "text.disabled" }} />
              <Typography variant="caption" color="text.disabled">
                {group.last_run_at
                  ? `Last run ${moment(group.last_run_at).fromNow()}`
                  : "No runs yet"}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Fade>
  );
};

JobGroupCard.propTypes = {
  group: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
};

// ─── Skeleton Card ────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <Card
    elevation={0}
    sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", overflow: "hidden" }}
  >
    <Skeleton variant="rectangular" height={4} />
    <CardContent sx={{ p: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" gap={1.5} alignItems="center">
          <Skeleton variant="circular" width={40} height={40} />
          <Box>
            <Skeleton width={120} height={18} />
            <Skeleton width={80} height={14} />
          </Box>
        </Stack>
        <Skeleton width={70} height={24} sx={{ borderRadius: 3 }} />
      </Stack>
      <Skeleton width="100%" height={6} sx={{ borderRadius: 3, mb: 1 }} />
      <Stack direction="row" gap={1}>
        <Skeleton width={60} height={22} sx={{ borderRadius: 3 }} />
        <Skeleton width={60} height={22} sx={{ borderRadius: 3 }} />
        <Skeleton width={60} height={22} sx={{ borderRadius: 3 }} />
      </Stack>
    </CardContent>
  </Card>
);

// ─── Success Fail Gradient ──────────────────────────────────────────────────────

const getSuccessFailGradient = (group) => {
  const total = group.total_count || 0;
  if (total === 0) return "linear-gradient(90deg, #9ca3af, #6b7280)";

  const segments = [
    { color: "#22c55e", count: group.completed_count || 0 },
    { color: "#3b82f6", count: group.running_count || 0 },
    { color: "#8b5cf6", count: group.processing_count || 0 },
    { color: "#f59e0b", count: group.pending_count || 0 },
    { color: "#ef4444", count: group.failed_count || 0 },
    { color: "#6b7280", count: group.cancelled_count || 0 },
  ].filter((s) => s.count > 0);

  if (segments.length === 0) return "linear-gradient(90deg, #9ca3af, #6b7280)";
  if (segments.length === 1)
    return `linear-gradient(90deg, ${segments[0].color}, ${segments[0].color})`;

  const gap = 3;
  const stops = [];
  let cumulative = 0;

  segments.forEach(({ color, count }, i) => {
    const pct = (count / total) * 100;
    const end = cumulative + pct;
    const isFirst = i === 0;
    const isLast = i === segments.length - 1;

    stops.push(`${color} ${isFirst ? 0 : Math.min(cumulative + gap, 100)}%`);
    stops.push(`${color} ${isLast ? 100 : Math.max(end - gap, 0)}%`);

    cumulative = end;
  });

  return `linear-gradient(90deg, ${stops.join(", ")})`;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const JobsGrouped = () => {
  const { api } = useAPI();
  const { errorState, showAlert } = useAlert();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");

  const fetchGrouped = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const res = await api.get("/classic/jobs/");
        setGroups(res.data?.data || []);
      } catch (err) {
        showAlert("error", err?.response?.data?.error || t("lang.something_went_wrong"));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [api]
  );

  useEffect(() => {
    fetchGrouped();
  }, []);

  const filtered = groups.filter((g) => g.name?.toLowerCase().includes(search.toLowerCase()));

  // Summary stats
  const totalGroups = groups.length;
  const totalJobs = groups.reduce((s, g) => s + (g.total_count || 0), 0);
  const activeJobs = groups.reduce(
    (s, g) => s + (g.running_count || 0) + (g.processing_count || 0),
    0
  );
  const failedJobs = groups.reduce((s, g) => s + (g.failed_count || 0), 0);

  return (
    <Box>
      {/* Summary cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            label="Job Types"
            value={loading ? "—" : totalGroups}
            icon={<JobIcon />}
            gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
            subtitle="Total job types"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            label="Total Runs"
            value={loading ? "—" : totalJobs.toLocaleString()}
            icon={<TrendingUp />}
            gradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
            subtitle="Total runs"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            label="Active Now"
            value={loading ? "—" : activeJobs}
            icon={<PlayCircleOutline />}
            gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
            subtitle="Running / Processing"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            label="Failed"
            value={loading ? "—" : failedJobs}
            icon={<ErrorOutline />}
            gradient={
              failedJobs > 0
                ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                : "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
            }
            subtitle="Total failed runs"
          />
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5} gap={2}>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          {t("lang.jobs") || "Jobs"}
          {!loading && (
            <Typography component="span" variant="body2" color="text.secondary" ml={1}>
              ({filtered.length} {filtered.length === 1 ? "type" : "types"})
            </Typography>
          )}
        </Typography>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip title={t("lang.reset") || "Refresh"} arrow>
            <IconButton
              onClick={() => fetchGrouped(true)}
              disabled={refreshing}
              sx={{
                border: "1.5px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 0.75,
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  transition: "transform 0.5s",
                  transform: refreshing ? "rotate(360deg)" : "rotate(0deg)",
                }}
              />
            </IconButton>
          </Tooltip>
          <TextField
            size="small"
            placeholder={t("lang.actions.search") || "Search job types…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: 220,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "0.875rem",
              },
            }}
          />
        </Stack>
      </Stack>

      {/* Cards grid */}
      {loading ? (
        <Grid container spacing={2.5}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      ) : filtered.length === 0 ? (
        <Box
          sx={{
            py: 8,
            textAlign: "center",
            border: "1.5px dashed",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <JobIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            {search ? "No job types match your search." : "No job groups found."}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filtered.map((group) => (
            <Grid item xs={12} sm={6} md={4} key={group.name}>
              <JobGroupCard
                group={group}
                onClick={() => navigate(`/jobs/${encodeURIComponent(group.name)}`)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <AlertMessage {...errorState} />
    </Box>
  );
};

export default JobsGrouped;
